import React, { useState, useRef } from 'react';
import { Schueler } from '../../types';
import { useSchueler } from '../../contexts/SchuelerContext';
import { useEscapeKey } from '../../lib/hooks';
import { apiPut } from '../../lib/api';
import { toast } from '../../lib/toast';
import { BLUE } from '../../lib/constants';
import { LdSheet } from '../../components/ui/LdSheet';
import { Button } from '../../components/ld/Button';
import { LdAuswahl } from '../../components/ld/LdFenster';

export function SchuelerBearbeitenModal({ schueler: s, onClose }: { schueler: Schueler; onClose: () => void }) {
  const { patchSchueler } = useSchueler();
  useEscapeKey(onClose);
  const [laden,       setLaden]       = useState(false);
  const [fehler,      setFehler]      = useState('');
  const nameParts = s.name.split(' ');
  const [vorname,     setVorname]     = useState(nameParts[0] ?? '');
  const [nachname,    setNachname]    = useState(nameParts.slice(1).join(' ') ?? '');
  const [email,       setEmail]       = useState(s.email ?? '');
  const [elternEmail, setElternEmail] = useState(s.eltern_email ?? '');
  const [elternTel,   setElternTel]   = useState(s.eltern_telefon ?? '');
  const [geburtstag,  setGeburtstag]  = useState(s.geburtsdatum ?? '');
  const [geschlecht,  setGeschlecht]  = useState(s.geschlecht ?? '');
  const [foto,        setFoto]        = useState(s.foto ?? '');
  const fotoRef = useRef<HTMLInputElement>(null);

  function onFotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setFoto(ev.target?.result as string ?? '');
    reader.readAsDataURL(file);
  }

  async function speichern() {
    setLaden(true); setFehler('');
    try {
      const updated = await apiPut<any>(`/api/schueler/${s.id}`, {
        vorname:        vorname.trim(),
        nachname:       nachname.trim(),
        email:          email.trim() || null,
        eltern_email:   elternEmail.trim() || null,
        eltern_telefon: elternTel.trim() || null,
        geburtsdatum:   geburtstag.trim() || null,
        geschlecht:     geschlecht || null,
        foto:           foto || null,
      });
      patchSchueler(s.id, prev => ({
        ...prev,
        name:           `${updated.vorname} ${updated.nachname}`,
        email:          updated.email ?? undefined,
        eltern_email:   updated.eltern_email ?? undefined,
        eltern_telefon: updated.eltern_telefon ?? undefined,
        geburtsdatum:   updated.geburtsdatum ?? undefined,
        geschlecht:     updated.geschlecht ?? undefined,
        foto:           updated.foto ?? undefined,
      }));
      toast('Schüler/in gespeichert', 'success');
      onClose();
    } catch (err: any) {
      setFehler(err.message ?? 'Fehler');
    } finally {
      setLaden(false);
    }
  }

  // ld-form-Muster: soft-fill-Feld + einheitliches Label (Tokens, vgl. Gespräch-Sheet)
  const inp: React.CSSProperties = {
    width: '100%', padding: '11px 14px', borderRadius: 'var(--ld-radius-md)',
    border: '1px solid var(--ld-field-border)', fontSize: 14, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--ld-bg-input)',
    color: 'var(--ld-text-primary)',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: 'var(--ld-field-label)',
    letterSpacing: 0.2, marginBottom: 6, display: 'block',
  };

  return (
    <LdSheet open={true} onClose={onClose} title="Schüler bearbeiten" maxWidth={520}>
      <div style={{ padding: '0 20px 4px' }}>
        {/* Foto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <div onClick={() => fotoRef.current?.click()} style={{
            width: 72, height: 72, borderRadius: '50%', cursor: 'pointer',
            background: foto ? 'transparent' : `linear-gradient(135deg, ${BLUE}, #5856D6)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0, border: '2px dashed #E5E5EA',
          }}>
            {foto
              ? <img src={foto} alt="Foto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 24, fontWeight: 800, color: 'white' }}>{s.avatar}</span>
            }
          </div>
          <div>
            <button onClick={() => fotoRef.current?.click()} style={{ minHeight: 44, padding: '7px 14px', background: 'var(--ld-bg-app)', border: 'none', borderRadius: 'var(--ld-radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Foto hochladen
            </button>
            {foto && <button onClick={() => setFoto('')} style={{ minHeight: 44, marginLeft: 8, padding: '7px 14px', background: '#FFF0EF', border: 'none', borderRadius: 'var(--ld-radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#FF6B6B' }}>Entfernen</button>}
          </div>
          <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFotoChange} />
        </div>

        {/* Name */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Vorname</label>
            <input className="ld-form-control" style={inp} value={vorname} onChange={e => setVorname(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Nachname</label>
            <input className="ld-form-control" style={inp} value={nachname} onChange={e => setNachname(e.target.value)} />
          </div>
        </div>

        {/* Geburtsdatum + Geschlecht */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Geburtsdatum</label>
            <input className="ld-form-control" style={inp} placeholder="TT.MM.JJJJ" value={geburtstag} onChange={e => setGeburtstag(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Geschlecht</label>
            <LdAuswahl value={geschlecht} onChange={e => setGeschlecht(e.target.value)}>
              <option value="">– wählen –</option>
              <option value="m">männlich</option>
              <option value="w">weiblich</option>
              <option value="d">divers</option>
            </LdAuswahl>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>E-Mail Schüler/in</label>
          <input className="ld-form-control" style={inp} type="email" placeholder="schueler@schule.de" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...lbl, color: BLUE }}>E-Mail Eltern</label>
          <input className="ld-form-control" style={{ ...inp, borderColor: elternEmail ? '#4ECDC4' : 'var(--ld-field-border)' }} type="email" placeholder="eltern@example.de" value={elternEmail} onChange={e => setElternEmail(e.target.value)} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Telefon Eltern</label>
          <input className="ld-form-control" style={inp} type="tel" placeholder="+49 ..." value={elternTel} onChange={e => setElternTel(e.target.value)} />
        </div>

        {fehler && <div style={{ background: '#FFF0EF', color: '#FF6B6B', borderRadius: 'var(--ld-radius-sm)', padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{fehler}</div>}

        {/* sticky Footer */}
        <div style={{ position: 'sticky', bottom: 0, display: 'flex', gap: 10, paddingTop: 14, paddingBottom: 4, marginTop: 4, background: 'var(--ld-bg-card)' }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Abbrechen</Button>
          <Button variant="primary" onClick={speichern} loading={laden} disabled={laden || !vorname.trim() || !nachname.trim()} style={{ flex: 2 }}>Speichern</Button>
        </div>
      </div>
    </LdSheet>
  );
}
